/**
 * Envio de relatório de erro a partir do browser.
 *
 * MÓDULO CLIENT-SAFE: nada de `node:*`, nada de `nodemailer`, nada de
 * `server-only`. Este é o único arquivo de alerta que um componente `"use client"`
 * pode importar.
 *
 * Estratégia de transporte, em ordem:
 *  1. `navigator.sendBeacon` — survives o unload da página, que é exatamente o
 *     caso mais comum (erro durante navegação).
 *  2. `fetch` com `keepalive` — fallback onde `sendBeacon` não existe.
 *  3. `console.error` — último recurso. Nunca lançamos: reportar um erro não
 *     pode gerar um erro novo.
 */

export interface ClientErrorInput {
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  digest?: string;
  context?: Record<string, string | number | boolean | null | undefined>;
}

const ENDPOINT = "/api/report-client-error";

/**
 * Monta o corpo do report.
 *
 * `userAgent` NÃO vai aqui: é forjável pelo client e, além disso, a rota lê o
 * header `user-agent` da request — que é a versão real. `navigator.userAgent`
 * divergindo do header é caso patológico, e mandá-lo seria aceitar dado do
 * atacante como se fosse de confiança.
 */
function buildBody(input: ClientErrorInput): string {
  return JSON.stringify({
    source: "client",
    message: input.message,
    stack: input.stack,
    componentStack: input.componentStack,
    url: input.url ?? window.location.href,
    digest: input.digest,
    context: input.context,
    timestamp: new Date().toISOString(),
  });
}

export function sendClientErrorReport(input: ClientErrorInput): boolean {
  if (typeof window === "undefined") return false;
  if (!input.message || !input.message.trim()) return false;

  let body: string;
  try {
    body = buildBody(input);
  } catch {
    // `JSON.stringify` lança em objeto circular dentro de `context`.
    return false;
  }

  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      // sendBeacon enfileira fora do ciclo de vida da página; `true` aqui
      // significa "enfileirado", não "entregue" — é o melhor sinal possível.
      const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
      if (navigator.sendBeacon(ENDPOINT, blob)) return true;
    }
  } catch {
    // Cai para o fetch.
  }

  try {
    void fetch(ENDPOINT, {
      method: "POST",
      body,
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      keepalive: true,
    }).catch(() => {
      /* fire-and-forget */
    });
    return true;
  } catch {
    console.error("[client-error-report] falha ao reportar erro:", input.message);
    return false;
  }
}
